<template>
  <div class="reset-password-page">
    <div class="reset-password-shell">
      <div class="brand">
        <img
          class="brand-logo"
          src="http://usdb.oss-us-west-1.aliyuncs.com/web_images/firmware-update/NEEWER@2x.png"
          alt="NEEWER"
        />
      </div>

      <section class="reset-card">
        <h1 class="title">Reset Password</h1>

        <a-alert
          v-if="status.visible"
          :type="status.type"
          :message="status.message"
          show-icon
          class="status-alert"
        />

        <a-form-model
          ref="resetForm"
          :model="form"
          :rules="rules"
          layout="vertical"
          hide-required-mark
          class="reset-form"
        >
          <a-form-model-item label="Email Address" prop="email">
            <a-input
              v-model="form.email"
              size="large"
              disabled
              placeholder="Your email will be filled from the reset link"
            />
          </a-form-model-item>

          <a-form-model-item label="New Password" prop="password">
            <a-input-password
              v-model="form.password"
              size="large"
              placeholder="Enter a new password with 8-20 characters"
            />
          </a-form-model-item>

          <a-form-model-item label="Confirm New Password" prop="confirmPassword">
            <a-input-password
              v-model="form.confirmPassword"
              size="large"
              placeholder="Re-enter your new password"
            />
          </a-form-model-item>

          <a-form-model-item label="Email Verification Code" prop="emailCode">
            <div class="code-row">
              <a-input
                v-model="form.emailCode"
                size="large"
                maxlength="6"
                placeholder="Enter the 6-digit code"
              />
              <a-button
                class="code-btn"
                size="large"
                :disabled="sendCodeDisabled"
                :loading="sendingCode"
                @click="handleSendCode"
              >
                {{ codeButtonText }}
              </a-button>
            </div>
          </a-form-model-item>

          <div class="actions">
            <a-button
              type="primary"
              size="large"
              block
              class="submit-btn"
              :loading="submitting"
              :disabled="submitDisabled"
              @click="handleSubmit"
            >
              Reset Password
            </a-button>

            <a-button
              v-if="showBackButton"
              size="large"
              block
              class="back-btn"
              @click="goToLogin"
            >
              Back to Login
            </a-button>
          </div>
        </a-form-model>
      </section>
    </div>
  </div>
</template>

<script>
import { resetPassword, sendResetCode } from "@/api";

export default {
  name: "ResetPassword",
  props: {
    loginPage: {
      type: String,
      default: "login.html"
    }
  },
  data() {
    const validateEmailCode = (rule, value, callback) => {
      if (!/^\d{6}$/.test(value || "")) {
        callback(new Error("Please enter a valid 6-digit email verification code."));
        return;
      }
      callback();
    };

    const validatePassword = (rule, value, callback) => {
      if (!/^.{8,20}$/.test(value || "")) {
        callback(new Error("Password must be between 8 and 20 characters."));
        return;
      }

      if (!/^(?=.*[A-Za-z])(?=.*\d).+$/.test(value || "")) {
        callback(new Error("Password must include both letters and numbers."));
        return;
      }

      callback();
    };

    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error("Please confirm your new password."));
        return;
      }

      if (value !== this.form.password) {
        callback(new Error("The two password entries do not match."));
        return;
      }

      callback();
    };

    return {
      token: "",
      sendingCode: false,
      submitting: false,
      showBackButton: false,
      countdown: 0,
      countdownTimer: null,
      status: {
        visible: false,
        type: "warning",
        message: ""
      },
      form: {
        email: "",
        password: "",
        confirmPassword: "",
        emailCode: ""
      },
      rules: {
        password: [{ validator: validatePassword, trigger: "change" }],
        confirmPassword: [{ validator: validateConfirmPassword, trigger: "change" }],
        emailCode: [{ validator: validateEmailCode, trigger: "change" }]
      }
    };
  },
  computed: {
    submitDisabled() {
      return !this.token;
    },
    sendCodeDisabled() {
      return !this.token || !this.form.email || this.countdown > 0 || this.sendingCode;
    },
    codeButtonText() {
      if (this.sendingCode) {
        return "Sending...";
      }

      return this.countdown > 0 ? this.countdown + "s" : "Get Code";
    }
  },
  created() {
    this.token = this.$route.query.token || "";
    this.form.email = this.$route.query.email || "";

    if (!this.token) {
      this.showStatus("warning", "If this link has expired, please start the forgot password process again.");
      return;
    }

    if (!this.form.email) {
      this.showStatus("warning", "The email parameter is missing. Please make sure the reset link is complete.");
    }
  },
  beforeDestroy() {
    this.clearCountdown();
  },
  methods: {
    showStatus(type, message) {
      this.status.visible = true;
      this.status.type = type;
      this.status.message = message;
    },
    clearStatus() {
      this.status.visible = false;
      this.status.message = "";
    },
    async handleSendCode() {
      if (!this.token) {
        this.showStatus("warning", "If this link has expired, please start the forgot password process again.");
        return;
      }

      if (!this.form.email) {
        this.showStatus("warning", "The email parameter is missing. Please make sure the reset link is complete.");
        return;
      }

      if (this.sendCodeDisabled) {
        return;
      }

      this.clearStatus();
      this.sendingCode = true;

      try {
        await sendResetCode({
          emailName: this.form.email,
          vtype: 2
        });

        this.showStatus("success", "A verification code has been sent to your email. Enter it below to continue.");
        if (this.$message) {
          this.$message.success("Verification code sent.");
        }
        this.startCountdown(60);
      } catch (error) {
        const message = this.getErrorMessage(error, "Failed to send the verification code. Please try again later.");
        this.showStatus("warning", message);
        if (this.$message) {
          this.$message.error(message);
        }
      } finally {
        this.sendingCode = false;
      }
    },
    handleSubmit() {
      if (!this.token) {
        this.showStatus("warning", "If this link has expired, please start the forgot password process again.");
        return;
      }

      if (!this.form.email) {
        this.showStatus("warning", "The email parameter is missing. Please make sure the reset link is complete.");
        return;
      }

      this.clearStatus();

      this.$refs.resetForm.validate(async (valid) => {
        if (!valid) {
          return false;
        }

        this.submitting = true;

        try {
          await resetPassword({
            token: this.token,
            emailName: this.form.email,
            verificationCode: this.form.emailCode,
            newPwd: this.form.password
          });

          this.showStatus("success", "Your password has been reset successfully. Return to the login page and sign in with your new password.");
          if (this.$message) {
            this.$message.success("Password reset successfully.");
          }
          this.showBackButton = true;
          this.resetFormValues();
        } catch (error) {
          const message = this.getErrorMessage(error, "Password reset failed. Please try again later.");
          this.showStatus("warning", message);
          if (this.$message) {
            this.$message.error(message);
          }
        } finally {
          this.submitting = false;
        }

        return true;
      });
    },
    resetFormValues() {
      const email = this.form.email;
      this.form.password = "";
      this.form.confirmPassword = "";
      this.form.emailCode = "";
      this.form.email = email;
      this.clearCountdown();
      this.$nextTick(() => {
        if (this.$refs.resetForm) {
          this.$refs.resetForm.clearValidate();
        }
      });
    },
    goToLogin() {
      window.location.href = this.loginPage;
    },
    startCountdown(seconds) {
      this.countdown = seconds;
      this.clearCountdown(false);
      this.countdownTimer = window.setInterval(() => {
        this.countdown -= 1;

        if (this.countdown <= 0) {
          this.clearCountdown();
        }
      }, 1000);
    },
    clearCountdown(resetValue = true) {
      if (this.countdownTimer) {
        window.clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }

      if (resetValue) {
        this.countdown = 0;
      }
    },
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return error && error.message ? error.message : fallback;
    }
  }
};
</script>

<style scoped>
.reset-password-page {
  min-height: 100vh;
  padding: 24px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.85), transparent 36%),
    linear-gradient(145deg, #f6efe6, #dbe7f2);
}

.reset-password-shell {
  width: 100%;
  max-width: 420px;
}

.brand {
  margin-bottom: 16px;
  text-align: center;
}

.brand-logo {
  width: 187px;
  height: 68px;
  display: block;
  margin: 0 auto;
  object-fit: contain;
}

.reset-card {
  padding: 28px 20px 20px;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 20px 60px rgba(33, 53, 71, 0.16);
  backdrop-filter: blur(16px);
}

.title {
  margin: 0 0 20px;
  font-size: 28px;
  line-height: 1.2;
  text-align: center;
  color: #213547;
}

.status-alert {
  margin-bottom: 16px;
  border-radius: 14px;
}

.reset-form /deep/ .ant-form-item {
  margin-bottom: 16px;
}

.reset-form /deep/ .ant-form-item-label > label {
  font-size: 14px;
  font-weight: 600;
  color: #213547;
}

.reset-form /deep/ .ant-input,
.reset-form /deep/ .ant-btn {
  height: 48px;
  border-radius: 14px;
}

.reset-form /deep/ .ant-input[disabled] {
  color: rgba(33, 53, 71, 0.88);
  background: rgba(33, 53, 71, 0.04);
}

.reset-form /deep/ .ant-input {
  font-size: 16px;
}

.code-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 116px;
  gap: 10px;
}

.code-btn {
  color: #2563eb;
  border-color: rgba(37, 99, 235, 0.22);
  background: rgba(37, 99, 235, 0.08);
}

.code-btn:hover,
.code-btn:focus {
  color: #2563eb;
  border-color: rgba(37, 99, 235, 0.3);
  background: rgba(37, 99, 235, 0.14);
}

.actions {
  margin-top: 8px;
}

.submit-btn {
  border: 0;
  color: #ffffff;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  background: linear-gradient(135deg, #2563eb, #3b82f6);
}

.submit-btn:hover,
.submit-btn:focus {
  color: #ffffff;
  background: linear-gradient(135deg, #1d4ed8, #2563eb);
}

.back-btn {
  margin-top: 12px;
}

@media (max-width: 480px) {
  .reset-card {
    padding: 24px 16px 18px;
    border-radius: 20px;
  }

  .title {
    font-size: 24px;
  }

  .code-row {
    grid-template-columns: 1fr;
  }
}
</style>